import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import type { DocNode } from '@atlaskit/adf-schema';

import { extractContentFeatures } from '@atlassian/conversation-assistant';
import {
	ExperienceName as RovoExperienceName,
	useAnalytics,
} from '@atlassian/conversation-assistant-instrumentation';
import {
	useEditorRovoBridge,
	type AddCommandHandlers,
	type AdditionalContext,
	type BuildContentContextFn,
	type StreamAbortCallback,
	type StreamCallback,
	type StreamEndCallback,
	type StreamErrorCallback,
	type StreamRepairCallback,
	type StreamStartCallback,
	type StreamTimeoutCallback,
} from '@atlassian/editor-rovo-bridge';
import {
	ConfluenceDeepResearchPageCreateCommand,
	ConfluencePageInitCommand,
	ConfluenceSubmitPromptOnPageCreateCommand,
	PrepareContentForFinalizationCommand,
} from '@atlassian/rovo-content-bridge-api-commands';
import { sendAIFCExperienceTrackingToParent } from '@atlassian/rovo-content-bridge-api-experience-tracking-sender';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import {
	showCreateWithRovoModalCloseFeedbackFlag,
	FeedbackVariant,
} from '@confluence/smart-create/entry-points/showFeedbackFlag';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore';

import { DeepResearchCommandHandler } from '../command-handlers-new/deep-research-command-handler';
import { PageInitCommandHandler } from '../command-handlers-new/page-init-command-handler';
import { PrepareContentForFinalizationCommandHandler } from '../command-handlers-new/prepare-content-for-finalization-command-handler';
import { SubmitPromptCommandHandler } from '../command-handlers-new/submit-prompt-command-handler';
import type { UseRovoBridgeCommandListenerParams } from '../RovoBridgeCommandListener';
import { log } from '../utils/aifc-logging';
import { getDocument } from '../utils/bridge-document-utils';
import { useIsPageViewMode } from '../utils/useIsPageViewMode';

import { useMediaImageProcessor } from './useMediaImageProcessor';

export const useEditorRovoBridgeCommandListener = ({
	contentId,
	contentLocation,
	editorAPI,
	setEditorDisabled,
	setTitlePlaceholderState,
	onTitleChange,
	mediaProvider,
	fakeGenerateAIHeaderImage,
	generateAIHeaderImage,
	forceDraftSync,
	flags,
}: Omit<UseRovoBridgeCommandListenerParams, 'contentContext'> & WithFlagsProps) => {
	const contentType = 'page';
	const { sendAnalyticsEvent } = useAnalytics();
	const isViewMode = useIsPageViewMode();
	const intl = useIntl();
	const { showModal } = useDialogs();
	const { showSmartCreationModal } = useSmartCreationModalState();
	const { stagingAreaMode } = useStagingAreaState();

	const imageProcessor = useMediaImageProcessor({
		mediaProvider,
	});

	const reuploadImages = useCallback(
		async (adfDoc: DocNode): Promise<void> => {
			if (fg('platform_editor_aifc_editor_rovo_bridge_batch_1')) {
				const processImagesAndUpdateDocument = async () => {
					if (!imageProcessor?.processImagesInAdf) {
						throw new Error(
							'Image processor not available for image re-uploading in editor streaming',
						);
					}
					if (!editorAPI) {
						throw new Error('Editor API not available for image re-uploading in editor streaming');
					}

					const { error, hasChanges, processedAdf } =
						await imageProcessor.processImagesInAdf(adfDoc);

					if (error) {
						throw error;
					}

					if (hasChanges) {
						editorAPI?.core.actions.replaceDocument(processedAdf, {
							scrollIntoView: false,
						});
					}
				};

				try {
					await processImagesAndUpdateDocument();
				} catch (error) {
					void sendAIFCExperienceTrackingToParent({
						status: 'fail',
						actionType: 'page',
						actionId: contentId,
						contentContext: contentLocation,
						experienceName: RovoExperienceName.AIFC_ALL_DATA_HANDLED,
						error: (error as Error).message,
					});

					getMonitoringClient().submitError(error as Error, {
						attribution: Attribution.PAGE_MODES,
					});
				}
			} else {
				if (!imageProcessor?.processImagesInAdf || !editorAPI) {
					return;
				}

				// Process images in the background and update when ready
				imageProcessor
					.processImagesInAdf(adfDoc)
					.then(({ error, hasChanges, processedAdf }) => {
						if (error) {
							getMonitoringClient().submitError(error, {
								attribution: Attribution.PAGE_MODES,
							});
						} else if (hasChanges) {
							editorAPI?.core.actions.replaceDocument(processedAdf, {
								scrollIntoView: false,
							});
						}
					})
					.catch((error) => {
						// Editor already has the original content, so this is non-fatal
						getMonitoringClient().submitError(error, { attribution: Attribution.PAGE_MODES });
					});
			}
		},
		[imageProcessor, editorAPI, contentId, contentLocation],
	);

	const sendInitialCreationAnalytics = useCallback(async () => {
		try {
			let features;
			if (editorAPI) {
				const doc = await getDocument(editorAPI);
				if (doc) {
					features = extractContentFeatures(doc);
				}
			}

			sendAnalyticsEvent({
				eventType: 'track',
				actionSubject: 'smartCreateContent',
				action: 'initialContentCreated',
				attributes: {
					createdContentProduct: 'confluence',
					createdContentType: contentType,
					createdContentFeatures: features,
					createdContentMetadata: {
						contentId,
					},
				},
			});
		} catch (error) {
			getMonitoringClient().submitError(error, { attribution: Attribution.PAGE_MODES });
		}
	}, [editorAPI, contentId, contentType, sendAnalyticsEvent]);

	// Called once when streaming starts
	const onStreamStart: StreamStartCallback = useCallback(
		({ streamType }) => {
			setEditorDisabled(true);

			if (
				fg('platform_editor_aifc_editor_rovo_bridge_batch_1')
					? streamType === 'create' || streamType === 'update'
					: streamType === 'create' || streamType === 'deep-research-create'
			) {
				setTitlePlaceholderState?.(true);
				if (isExperimentEnabled('confluence_aifc_image_generation_experiment')) {
					if (!fg('aifc_header_image_pseudo_title_enabled')) {
						void fakeGenerateAIHeaderImage?.();
					}
				}
			}

			// Succeed the AIFC_INITIAL_DATA_RECEIVED experience since content has started streaming.
			// This ensures this experience won't timeout and then fail the AIFC_GENERATE_CONTENT or AIFC_UPDATE_CONTENT experience.
			// NOTE: This experience isn't started for all flows, it'll be a no-op if it's not started.
			void sendAIFCExperienceTrackingToParent({
				status: 'success',
				actionType: contentType,
				actionId: contentId,
				contentContext: contentLocation,
				experienceName: RovoExperienceName.AIFC_INITIAL_DATA_RECEIVED,
			});
		},
		[
			setEditorDisabled,
			fakeGenerateAIHeaderImage,
			setTitlePlaceholderState,
			contentId,
			contentType,
			contentLocation,
		],
	);

	// Called on every stream chunk
	const onStream: StreamCallback = useCallback(
		({ payload }) => {
			if ('title' in payload.data) {
				const title = payload?.data?.title;
				onTitleChange?.(title);
				if (title) {
					setTitlePlaceholderState?.(false);
				}
			}
		},
		[onTitleChange, setTitlePlaceholderState],
	);

	// Called when streaming ends
	const onStreamEnd: StreamEndCallback = useCallback(
		async ({ streamType, payload, adfDoc }) => {
			try {
				// Trigger image re-uploading if we have a document and handler
				if (adfDoc && streamType !== 'update') {
					if (fg('platform_editor_aifc_editor_rovo_bridge_batch_1')) {
						await reuploadImages(adfDoc);
					} else {
						void reuploadImages(adfDoc);
					}
				}

				if (streamType === 'create') {
					const title = 'title' in payload?.data ? payload.data.title : undefined;

					if (title) {
						if (isExperimentEnabled('confluence_aifc_image_generation_experiment')) {
							if (!fg('aifc_header_image_pseudo_title_enabled')) {
								void generateAIHeaderImage?.({ adfContent: title });
							}
						}
					}
				}

				// This experience success is a no-op if AIFC_ALL_DATA_HANDLED is not started. It may not be started for all flows.
				void sendAIFCExperienceTrackingToParent({
					status: 'success',
					actionType: contentType,
					actionId: contentId,
					contentContext: contentLocation,
					experienceName: RovoExperienceName.AIFC_ALL_DATA_HANDLED,
				});

				if (streamType === 'create' || streamType === 'deep-research-create') {
					void sendInitialCreationAnalytics();
				} else if (streamType === 'update') {
					if (!showSmartCreationModal && stagingAreaMode !== 'modal') {
						showCreateWithRovoModalCloseFeedbackFlag(
							flags,
							intl,
							showModal,
							FeedbackVariant.AFTER_UPDATE_STREAM_COMPLETE,
							contentType,
						);
					}
				}
			} finally {
				setEditorDisabled(false);
			}
		},
		[
			contentId,
			contentType,
			contentLocation,
			setEditorDisabled,
			reuploadImages,
			generateAIHeaderImage,
			sendInitialCreationAnalytics,
			flags,
			intl,
			showModal,
			showSmartCreationModal,
			stagingAreaMode,
		],
	);

	const onStreamAbort: StreamAbortCallback = useCallback(() => {
		setEditorDisabled?.(false);
	}, [setEditorDisabled]);

	const onStreamTimeout: StreamTimeoutCallback = useCallback(() => {
		setEditorDisabled?.(false);
	}, [setEditorDisabled]);

	const onStreamError: StreamErrorCallback = useCallback(
		({ error, streamType }) => {
			log('Error in command handler:', error);

			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'error',
				actionSubject: 'smartCreateContent',
				attributes: {
					contentType,
					error: error instanceof Error ? error.message : String(error),
					stack: error instanceof Error && error.stack ? error.stack : undefined,
					streamType,
					contentId,
				},
			});

			void sendAIFCExperienceTrackingToParent({
				status: 'fail',
				actionType: contentType,
				actionId: contentId,
				contentContext: contentLocation,
				experienceName: RovoExperienceName.AIFC_ALL_DATA_HANDLED,
				error: error instanceof Error ? error.message : String(error),
			});

			if (fg('platform_editor_ai_aifc_patch_beta_2')) {
				setEditorDisabled(false);
			}
		},
		[contentId, contentType, contentLocation, sendAnalyticsEvent, setEditorDisabled],
	);

	const onStreamRepair: StreamRepairCallback = useCallback(
		({ streamType, repairs }) => {
			sendAnalyticsEvent({
				eventType: 'operational',
				actionSubject: 'smartCreateContent',
				action: 'adfRepaired',
				attributes: {
					contentType,
					contentId,
					streamType,
					repairCount: repairs.length,
					// Manually selecting properties to avoid sending UGC
					// in case more properties are added in the future.
					repairs: repairs.map((repair) => ({
						type: repair.type,
						from: repair.from,
						to: repair.to,
						context: repair.context,
						expansion: repair.expansion,
					})),
				},
			});
		},
		[contentId, contentType, sendAnalyticsEvent],
	);

	const additionalContext = useMemo<AdditionalContext>(() => {
		return {
			isViewMode,
		};
	}, [isViewMode]);

	const buildContentContext: BuildContentContextFn = useCallback(
		({ doc, selectionFragment, selectionLocalIds, selectionMarkdown }) => {
			return {
				contentId,
				contentType,
				isViewMode,
				pageBody: doc,
				selectionFragment,
				selectionLocalIds,
				selectionMarkdown,
			};
		},
		[contentId, contentType, isViewMode],
	);

	const addCommandHandlers: AddCommandHandlers = useCallback(
		({ commandManager }) => {
			commandManager
				.addDefaultCommandHandlers()
				.addCommandHandler(
					ConfluenceDeepResearchPageCreateCommand,
					new DeepResearchCommandHandler(commandManager.commandProps),
				)
				.addCommandHandler(
					ConfluencePageInitCommand,
					new PageInitCommandHandler(commandManager.commandProps, { reuploadImages }),
				)
				.addCommandHandler(
					PrepareContentForFinalizationCommand,
					new PrepareContentForFinalizationCommandHandler(commandManager.commandProps, {
						forceDraftSync,
					}),
				)
				.addCommandHandler(
					ConfluenceSubmitPromptOnPageCreateCommand,
					new SubmitPromptCommandHandler(commandManager.commandProps, { generateAIHeaderImage }),
				);
		},
		[reuploadImages, forceDraftSync, generateAIHeaderImage],
	);

	useEditorRovoBridge({
		contentLocation,
		contentId,
		editorAPI,
		addCommandHandlers,
		additionalContext,
		buildContentContext: fg('platform_editor_aifc_editor_rovo_bridge_batch_1')
			? undefined
			: buildContentContext,
		onStreamStart,
		onStream,
		onStreamEnd,
		onStreamAbort,
		onStreamTimeout,
		onStreamError,
		onStreamRepair,
	});
};
